import React from 'react';
import {Referral} from 'placement/domains';
import {ServiceLocationEnumNameToValue, TreatmentConsiderEnumNameToValue} from 'placement/const';
import {EstimatedServiceHoursSection} from './EstimatedServiceHoursSection';
import {BehaviorSubject} from 'rxjs';
import {useObservable} from "../../rxsupport";

type Props = {
    referrals: Array<Referral>;
    selectedReferral$: BehaviorSubject<Referral | null>;
}


type providerSiblingBarProps = {
    referrals: Array<Referral>;
    selectedReferral$: BehaviorSubject<Referral | null>;
}

type providerOfferReferralDetailProps = {
    referral: Referral;
}

function ProviderOfferReferralDetail({referral}: providerOfferReferralDetailProps) {
    return (
        <div className='h-full bg-white shadow-md scrollbar-always'>
            <div className='p-5 flex-auto'>

                <EstimatedServiceHoursSection referral={referral}/>

                {referral?.serviceLocations && referral.serviceLocations.length > 0 &&
                    <div className='pb-5'>
                        <div className='font-semibold pb-5'>
                            Service Location
                        </div>
                        <ul className='pl-5'>
                            {
                                referral?.serviceLocations
                                    .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                    .filter((v) => v !== undefined)
                                    .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                            }
                        </ul>
                    </div>
                }
                {
                    referral?.serviceLocationsParaprofessional && referral.serviceLocationsParaprofessional.length > 0 &&
                    <div className='pb-4'>
                        <div className='font-semibold pb-5'>
                            Service Location Paraprofessional
                        </div>
                        <ul className='pl-5'>
                            {
                                referral?.serviceLocationsParaprofessional
                                    .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                    .filter((v) => v !== undefined)
                                    .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                            }
                        </ul>
                    </div>
                }
                {
                    referral?.serviceLocationsMidLevel && referral.serviceLocationsMidLevel.length > 0 &&
                    <div className='pb-4'>
                        <div className='font-semibold pb-5'>
                            Service Location Mid-Level
                        </div>
                        <ul className='pl-5'>
                            {
                                referral?.serviceLocationsMidLevel
                                    .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                    .filter((v) => v !== undefined)
                                    .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                            }
                        </ul>
                    </div>
                }
                {
                    referral?.serviceLocationsHighLevel && referral.serviceLocationsHighLevel.length > 0 &&
                    <div className='pb-4'>
                        <div className='font-semibold pb-5'>
                            Service Location High-Level
                        </div>
                        <ul className='pl-5'>
                            {
                                referral?.serviceLocationsHighLevel
                                    .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                    .filter((v) => v !== undefined)
                                    .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                            }
                        </ul>
                    </div>
                }
                {
                    referral?.serviceLocationsGroupCare && referral.serviceLocationsGroupCare.length > 0 &&
                    <div className='pb-4'>
                        <div className='font-semibold pb-5'>
                            Service Location Group Care
                        </div>
                        <ul className='pl-5'>
                            {
                                referral?.serviceLocationsGroupCare
                                    .map(ServiceLocationEnumNameToValue.get.bind(ServiceLocationEnumNameToValue))
                                    .filter((v) => v !== undefined)
                                    .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                            }
                        </ul>
                    </div>
                }

                <div className='pb-5'>
                    <div className='font-semibold pb-5'>
                        Hours Notes
                    </div>
                    <div className=''>
                        {referral?.serviceNotes ? referral?.serviceNotes.split('\n').map((i, key) => {
                            return <div className='pb-2' key={key}>{i}</div>;
                        }) : 'None'}
                    </div>
                </div>
                <div className='pb-5'>
                    <div className='font-semibold pb-5'>
                        Schedule Notes
                    </div>
                    <div className=''>
                        {referral?.scheduleNotes ? referral?.scheduleNotes.split('\n').map((i, key) => {
                            return <div className='pb-2' key={key}>{i}</div>;
                        }) : 'None'}
                    </div>
                </div>
                <div className=''>
                    <div className='font-semibold pb-5'>
                        Treatment Considerations
                    </div>
                    {referral?.referralConsiderations && referral.referralConsiderations.length > 0 ?
                        <ul className='pl-5'>
                            {
                                referral?.referralConsiderations
                                    .map(TreatmentConsiderEnumNameToValue.get.bind(TreatmentConsiderEnumNameToValue))
                                    .filter((v) => v !== undefined)
                                    .sort((a, b) => (a as string).localeCompare(b as string))
                                    .map((v, key) => <li className='pb-2 list-disc' key={key}>{v}</li>)
                            }
                        </ul> : <div>None</div>
                    }
                </div>
            </div>
            {/*footer*/}
        </div>
    );
}

function ProviderSiblingBar({referrals, selectedReferral$}: providerSiblingBarProps) {
    return (
        <div className={"h-12 flex border-b"}>
            {
                referrals.map((referral, index) =>
                    <ProviderSiblingBarItem
                        key={referral.guid}
                        referral={referral}
                        isLast={index === (referrals.length - 1)}
                        isSelected={referral.guid === selectedReferral$.getValue()!.guid}
                        onClick={() => selectedReferral$.next(referrals[index])}
                    />)
            }
        </div>
    );
}

type ProviderSiblingBarItemProps = {
    referral: Referral;
    isLast: boolean;
    isSelected: boolean;
    onClick: (guid: string) => void;
}

function ProviderSiblingBarItem({referral, onClick, isSelected, isLast}: ProviderSiblingBarItemProps) {
    const selectedCss = "font-medium text-white bg-blue-portal-link";
    const notSelectedCss = "font-normal text-black bg-white";

    return <div key={referral.guid}
                onClick={() => onClick(referral.guid)}
                className={`flex justify-center items-center flex-grow text-xl border-l cursor-pointer
                         ${isSelected ? selectedCss : notSelectedCss} 
                         ${isLast ? 'border-r' : ''} `}>
        {referral.clientName}
    </div>;
}

export function ProviderOfferDetailLeftRail({referrals, selectedReferral$}: Props) {
    const currentSelected = useObservable<Referral | null>(selectedReferral$, selectedReferral$.getValue());

    return (
        <div className='h-full pl-5 pr-5 pb-5 float-left placement-provider-offer-detail-left-rail-width'
             data-testid="provider-offer-detail-left-rail"
        >
            {referrals.length > 1 && <ProviderSiblingBar referrals={referrals}
                                                         selectedReferral$={selectedReferral$}/>}
            <ProviderOfferReferralDetail referral={currentSelected!}/>
        </div>
    );
}
